<script lang="ts" setup>
import { type GetEditorialContentsEditorialLocationResponse } from '@/api';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { Constants } from '@/utilities/constants';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';

const props = defineProps<{
  location: GetEditorialContentsEditorialLocationResponse;
  categorySlug: string;
  index?: number;
}>();

const { t } = useI18n();

// The thumbnail is a fixed size, so the browser only ever needs the smallest variants
const thumbnailSizes = '(max-width: 639px) 112px, 128px';

const fetchPriority = computed(() => (props.index === 0 ? 'high' : 'auto'));
const loadingPriority = computed(() => ((props.index ?? 99) < 2 ? 'eager' : 'lazy'));

// Computed property to determine the correct route
// Routes to child locations when parent location information is available
const locationRoute = computed(() => {
  // Check if location has parent information
  const hasParent = props.location.parentLocation?.slug;

  if (hasParent && props.location.parentLocation) {
    // Route to child location
    return {
      name: 'editorial-child-location',
      params: {
        categorySlug: props.categorySlug,
        parentLocationSlug: props.location.parentLocation.slug,
        childLocationSlug: props.location.slug,
      },
    };
  } else {
    // Route to regular location
    return {
      name: 'editorial-location',
      params: {
        categorySlug: props.categorySlug,
        locationSlug: props.location.slug,
      },
    };
  }
});

// Une ville n'a pas de sous-villes : le raccourci n'a de sens que sur une région,
// qui est le seul cas où parentLocation est absent.
const isRegion = computed(() => !props.location.parentLocation);

const childLocationsRoute = computed(() => ({
  ...locationRoute.value,
  hash: `#${Constants.ScrollAnchors.EditorialChildLocations}`,
}));
</script>

<template>
  <!-- Grille à zones nommées : titre pleine largeur puis image+texte puis actions sur mobile ; image à gauche et le reste en colonne dès sm -->
  <div class="editorial-card group hover:border-ph-green-300 relative grid gap-x-4 gap-y-2 rounded-xl border border-gray-200 bg-white p-3 transition duration-300 hover:shadow-md">
    <!-- Titre -->
    <h3 class="area-title group-hover:text-ph-green-700 text-base leading-snug font-semibold text-gray-900 transition-colors">
      {{ location.name }}
    </h3>

    <!-- Image -->
    <!-- aspect-3/2 (ratio natif) plutôt que le plugin aspect-w/h, dont le padding en % se calcule sur la largeur du parent -->
    <div class="area-image aspect-3/2 w-full self-start overflow-hidden rounded-lg">
      <img
        v-if="location.heroImage"
        :alt="location.title"
        :fetchpriority="fetchPriority"
        :height="Constants.ImageSizes.Small.Height"
        :loading="loadingPriority"
        :sizes="thumbnailSizes"
        :src="location.heroImage.resizedToSmall()"
        :srcset="location.heroImage.toSrcset()"
        :width="Constants.ImageSizes.Small.Width"
        class="h-full w-full object-cover transition duration-300 group-hover:scale-105"
      />
      <div v-else class="flex h-full w-full items-center justify-center bg-gray-200">
        <div class="text-xs text-gray-400">{{ t('views.editorial.no-image') }}</div>
      </div>
    </div>

    <!-- Résumé -->
    <!-- Le <p> est enveloppé : un enfant direct de la grille est « blocifié », ce qui casserait le line-clamp (display -webkit-box) -->
    <div class="area-text self-start">
      <div class="editorial-summary text-sm text-gray-600">
        {{ location.summary }}
      </div>
    </div>

    <!-- Actions : deux boutons 50/50 sur mobile ; bouton + séparateur + lien texte dès sm -->
    <div class="area-actions flex items-stretch gap-2 pt-1 text-sm font-medium sm:items-center sm:gap-x-5 sm:pt-0">
      <!-- Action principale : le clic est capté par le lien qui recouvre la carte, d'où le group-hover -->
      <span
        class="border-ph-green-300 text-ph-green-600 group-hover:border-ph-green-400 group-hover:bg-ph-green-50 group-hover:text-ph-green-800 inline-flex flex-1 items-center justify-center gap-1.5 rounded-md border bg-white px-3 py-1.5 transition-colors sm:flex-none"
      >
        {{ t('views.editorial.view-projects') }}
        <FontAwesomeIcon :icon="faChevronRight" class="text-[0.7em]" />
      </span>

      <span v-if="isRegion" aria-hidden="true" class="hidden h-5 w-px shrink-0 bg-gray-300 sm:block"></span>

      <!-- Choisir une ville : bouton fantôme 50 % (sans fond, bordure transparente pour égaliser la hauteur) sur mobile, simple lien texte dès sm -->
      <!-- z-10 pour passer au-dessus du lien qui recouvre la carte -->
      <RouterLink
        v-if="isRegion"
        :to="childLocationsRoute"
        class="text-ph-green-600 hover:text-ph-green-800 group-hover:text-ph-green-800 focus:ring-ph-green-500 relative z-10 flex flex-1 items-center justify-center rounded-md border border-transparent px-3 py-1.5 text-center transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-none sm:flex-none sm:justify-start sm:rounded-sm sm:border-0 sm:px-0 sm:py-1 sm:text-left"
      >
        {{ t('views.editorial.location.choose-city') }}
      </RouterLink>
    </div>

    <!-- Zone cliquable -->
    <RouterLink :to="locationRoute" class="focus:ring-ph-green-500 absolute inset-0 rounded-xl focus:ring-2 focus:ring-offset-2 focus:outline-none">
      <span class="sr-only">{{ t('views.editorial.view', { title: location.title }) }}</span>
    </RouterLink>
  </div>
</template>

<style lang="css" scoped>
.editorial-card {
  grid-template-columns: 100px minmax(0, 1fr);
  grid-template-areas:
    'title title'
    'image text'
    'actions actions';
}

.area-title {
  grid-area: title;
}

.area-image {
  grid-area: image;
}

.area-text {
  grid-area: text;
}

.area-actions {
  grid-area: actions;
}

/* line-clamp défini ici : le projet ne génère pas les utilitaires line-clamp de Tailwind */
.editorial-summary {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
}

/* Dès sm : image à gauche sur toute la hauteur, titre/texte/actions empilés à droite (disposition d'origine) */
@media (min-width: 640px) {
  .editorial-card {
    grid-template-columns: 8rem minmax(0, 1fr);
    grid-template-areas:
      'image title'
      'image text'
      'image actions';
  }

  .editorial-summary {
    -webkit-line-clamp: 2;
  }
}
</style>
